import { createStyles, Divider, Collapse, Flex, ChevronIcon } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { Game } from '@betterpool/api-types/games-service';

import dt from '~/testing';
import { Text } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';

import { useFormattedSlateDate } from './useFormattedSlateDate';

const useStyles = createStyles((theme) => ({
  divider: {
    borderTopColor: theme.colors.gray[2],
  },
  dateContainer: {
    marginBottom: theme.spacing.xs,
  },
  clickableRow: {
    marginTop: theme.spacing.xs,
    marginBottom: theme.spacing.xs,
    width: '100%',
  },
}));

type GameListProps = {
  games: Game[];
  slateDate?: string;
  slateLabel?: string;
  disableExpand?: boolean;
};

function GameListChevron({ opened }: { opened: boolean }) {
  return opened ? <ChevronIcon direction="up" /> : <ChevronIcon direction="down" />;
}

function GameList({ games = [], slateDate, slateLabel, disableExpand = false }: GameListProps) {
  const [opened, { toggle }] = useDisclosure(false);
  const { classes } = useStyles();
  const { formatSlateDate } = useFormattedSlateDate();
  const isExpandable = games.length > 0 && !disableExpand;
  const generatedSlateLabel = !isExpandable ? slateLabel : `${slateLabel} (${games.length} games)`;

  return (
    <>
      <Button
        variant="text"
        className={classes.clickableRow}
        data-test-id={dt.contestDetail.section.about.game.item.collapse}
        onClick={isExpandable ? toggle : undefined}
        disabled={!isExpandable}
        aria-disabled={!isExpandable}
      >
        <Flex justify="space-between" align="center">
          <Text variant="body-medium" fw={500}>
            {generatedSlateLabel}
          </Text>
          <Flex align="center" gap="xs">
            {slateDate && <Text variant="subhead-small">{slateDate}</Text>}
            {isExpandable ? <GameListChevron opened={opened} /> : null}
          </Flex>
        </Flex>
      </Button>
      {isExpandable &&
        games.map((game) => (
          <Collapse key={game.id} in={opened}>
            <Divider className={classes.divider} my="xs" />
            <Flex
              justify="space-between"
              align="center"
              data-test-id={dt.contestDetail.section.about.game.item.container}
              px="md"
            >
              <Text
                ml="md"
                variant="body-medium"
                data-test-id={dt.contestDetail.section.about.game.item.name}
              >
                {game.name}
              </Text>
              <Text variant="subhead-small" miw={100} ta="right">
                {formatSlateDate((game as Game & { startDate: string }).startDate)}
              </Text>
            </Flex>
          </Collapse>
        ))}
    </>
  );
}

export default GameList;
